import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download, Loader2, Trophy, Users, Shield, Medal, Crown, Star, Sparkles,
  LineChart, TrendingUp, TrendingDown, Minus, Scale, Info, BarChart3,
  CheckCircle2, Lock, AlertTriangle, Gift, UserPlus, Clock, Send,
  Share2, MessageCircle, MessageSquare, Mail, Copy, Link as LinkIcon,
} from "lucide-react";
import api, { apiError } from "../../lib/api";
import ServiceCV from "../../components/ServiceCV";
import { CoachingCard } from "../../components/ai";
import { ChangePasswordCard } from "../../components/ChangePassword";
import { MyDetailsCard } from "../../components/MyDetailsCard";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/common";

const REASONS = ["Incorrect interaction", "Abusive comment", "Discriminatory comment", "Fraudulent feedback", "Customer did not interact with me", "Other"];
const BADGE_ICONS = { Trophy, Users, Shield, Medal, Crown, Star, Sparkles, LineChart, TrendingUp };

export default function EmployeePortal() {
  const [cv, setCv] = useState(null);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [detail, setDetail] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState(null);

  const load = () => {
    api.get("/me/cv").then((r) => setCv(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/me/dashboard").then((r) => setDashboard(r.data)).catch(() => {});
    api.get("/me/referrals").then((r) => setReferrals(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  // Live-refresh when the employee saves their own profile from the Account tab.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("sv:profile-updated", h);
    return () => window.removeEventListener("sv:profile-updated", h);
  }, []);

  const downloadCv = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/me/cv.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(cv?.employee?.name || "Service").replace(/\s+/g, "_")}_Servantage_CV.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your verified Service CV is downloading");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const submitDispute = async () => {
    try {
      await api.post("/disputes", { feedback_id: active.id, reason, detail: detail || null });
      toast.success("Dispute submitted for review"); setActive(null); setDetail(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!cv) return <div className="text-slate-400">Loading your profile…</div>;

  return (
    <div className="space-y-6" data-testid="employee-portal">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>My Profile</Label>
          <h1 className="flex flex-wrap items-center gap-3 font-head text-2xl font-bold text-slate-900 sm:text-3xl">
            <span>Hi{cv?.employee?.name ? `, ${cv.employee.name.split(" ")[0]}` : ""}</span>
            {dashboard?.badges?.earned > 0 && (
              <span data-testid="header-badge-pill"
                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                <Trophy className="h-3.5 w-3.5" /> {dashboard.badges.earned} badge{dashboard.badges.earned === 1 ? "" : "s"}
              </span>
            )}
            {dashboard?.headline?.ssi_percent != null && (
              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">
                SSI {dashboard.headline.ssi_percent}%
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Your personal performance view and verified Service CV.</p>
        </div>
        <Button onClick={downloadCv} disabled={downloading} data-testid="download-cv-btn"
          className="bg-[#4338CA] hover:bg-[#3730A3]">
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          {downloading ? "Generating…" : "Download verified CV"}
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="mr-1 h-4 w-4" />Dashboard</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="mr-1 h-4 w-4" />Leaderboard</TabsTrigger>
          <TabsTrigger value="cv" data-testid="tab-cv">My Service CV</TabsTrigger>
          <TabsTrigger value="refer" data-testid="tab-refer"><Gift className="mr-1 h-4 w-4" />Refer & earn</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Account</TabsTrigger>
        </TabsList>

        {/* DASHBOARD (now consolidated: KPIs + trend + strengths + themes + transparency + ratings drill-down) */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          {!dashboard ? <SkeletonBlock /> : <PersonalDashboard d={dashboard} />}
        </TabsContent>

        {/* LEADERBOARD */}
        <TabsContent value="leaderboard" className="mt-4 space-y-4">
          <LeaderboardTab />
        </TabsContent>

        {/* REFER & EARN */}
        <TabsContent value="refer" className="mt-4">
          {!referrals ? <SkeletonBlock /> : <ReferAndEarn r={referrals} onSubmitted={load} />}
        </TabsContent>

        {/* CV */}
        <TabsContent value="cv" className="mt-4">
          {cv?.employee?.id && <CoachingCard employeeId={cv.employee.id} />}
          <div className="mt-4"><ServiceCV cv={cv} onDispute={(f) => setActive(f)} /></div>
        </TabsContent>

        {/* ACCOUNT */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          <MyDetailsCard />
          <ChangePasswordCard />
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dispute this feedback</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Your manager will review this. Disputed feedback is never silently deleted.</p>
          <div className="mt-4 space-y-4">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="dispute-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Add any detail (optional)" value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="dispute-detail" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={submitDispute} data-testid="submit-dispute" className="bg-[#4338CA] hover:bg-[#3730A3]">Submit dispute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========================= DASHBOARD =========================
function PersonalDashboard({ d }) {
  const h = d.headline;
  const r = d.ratings;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Service Index" value={`${h.ssi_percent}%`} testid="d-ssi" />
        <StatTile label="Avg Rating" value={h.raw_average.toFixed(2)} testid="d-avg" />
        <StatTile label="Verified Ratings" value={h.verified} testid="d-verified" />
        <StatTile label="Confidence" value={`${Math.round((h.confidence || 0) * 100)}%`} testid="d-conf" />
      </div>

      <TrendCard trend={d.trend_30d} summary={d.trend_summary} />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid="strengths-card">
          <div className="mb-2 flex items-center gap-2 font-head text-sm font-bold text-emerald-700">
            <Sparkles className="h-4 w-4" /> Your strengths
          </div>
          {d.strengths.length === 0 ? (
            <p className="text-sm text-slate-500">We'll surface strengths once you have more rated touchpoints.</p>
          ) : d.strengths.map((s) => (
            <div key={s.name} className="mb-2 flex items-center justify-between rounded-lg bg-white p-2 shadow-sm">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500">from {s.sample} ratings</div>
              </div>
              <div className="font-head text-lg font-bold text-emerald-600">{s.score}%</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4" data-testid="improvements-card">
          <div className="mb-2 flex items-center gap-2 font-head text-sm font-bold text-amber-700">
            <TrendingUp className="h-4 w-4" /> Improvement areas
          </div>
          {d.improvements.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing flagged — you're above the alert line on every touchpoint.</p>
          ) : d.improvements.map((s) => (
            <div key={s.name} className="mb-2 flex items-center justify-between rounded-lg bg-white p-2 shadow-sm">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500">from {s.sample} ratings</div>
              </div>
              <div className="font-head text-lg font-bold text-amber-600">{s.score}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="themes-card">
        <div className="mb-2 flex items-center gap-2 font-head text-sm font-bold text-slate-900">
          <BarChart3 className="h-4 w-4" /> Feedback themes
        </div>
        <p className="mb-3 text-xs text-slate-500">Words customers keep using about your service.</p>
        {d.themes.length === 0 ? (
          <p className="text-sm text-slate-500">Not enough comments yet to detect themes. Keep going!</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {d.themes.map((t) => (
              <span key={t.word} data-testid={`theme-${t.word}`} className="rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">
                {t.word} <span className="text-xs text-indigo-500">· {t.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {d.monthly_series.length > 0 && (
        <MonthlyProgressCard series={d.monthly_series} />
      )}

      {/* ========== TRANSPARENCY (merged from the former Fair play tab) ========== */}
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <Scale className="h-5 w-5 text-indigo-600" />
          <h2 className="font-head text-lg font-bold text-slate-900">Transparency & fair play</h2>
        </div>
        <p className="text-sm leading-relaxed text-slate-600">
          Everything about how your score is built is visible to you. No silent deletes. Every rating, every dispute, every step of the math — right below.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total ratings" value={r.total_received} testid="fp-total" />
        <StatTile label="Verified" value={r.verified} testid="fp-verified" tone="emerald" />
        <StatTile label="Rejected" value={r.rejected} testid="fp-rejected" tone="rose" />
        <StatTile label="Disputed (open)" value={r.disputed_open} testid="fp-disputed-open" tone="amber" />
      </div>

      <ConfidenceCard band={d.confidence_band} score={d.score} />
      <ScoreExplainerCard steps={d.score_explainer} />
      <DisputesCard disputes={d.recent_disputes} ratings={r} />

      {/* ========== RATINGS DRILL-DOWN ========== */}
      <RatingsDrilldown />
    </>
  );
}

// ========================= RATINGS DRILL-DOWN =========================
function RatingsDrilldown() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [open, setOpen] = useState(null); // currently expanded row id

  useEffect(() => {
    const q = filter === "ALL" ? "/me/ratings?limit=200" : `/me/ratings?status=${filter}&limit=200`;
    api.get(q).then((r) => setData(r.data)).catch(() => {});
  }, [filter]);

  if (!data) return <div className="h-20 animate-pulse rounded-xl bg-slate-100" />;

  const trustPill = (ts) => {
    const score = Number(ts ?? 1);
    if (score >= 0.75) return { label: `Trust ${Math.round(score * 100)}%`, tone: "emerald" };
    if (score >= 0.4) return { label: `Trust ${Math.round(score * 100)}%`, tone: "amber" };
    return { label: `Trust ${Math.round(score * 100)}%`, tone: "rose" };
  };
  const statusTone = { TRUSTED: "emerald", APPROVED: "emerald", REVIEW: "amber", PENDING: "amber", REJECTED: "rose" };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ratings-drilldown">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="font-head text-sm font-bold text-slate-900">Every rating you've received</div>
        <div className="flex gap-1">
          {["ALL", "TRUSTED", "REVIEW", "REJECTED"].map((s) => (
            <button key={s} data-testid={`filter-${s.toLowerCase()}`} onClick={() => setFilter(s)}
              className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${filter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {s === "ALL" ? "All" : s[0] + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg bg-emerald-50 p-2"><strong className="text-emerald-700">{data.trust_buckets.high}</strong> high-trust</div>
        <div className="rounded-lg bg-amber-50 p-2"><strong className="text-amber-700">{data.trust_buckets.medium}</strong> medium</div>
        <div className="rounded-lg bg-rose-50 p-2"><strong className="text-rose-700">{data.trust_buckets.low}</strong> low-trust</div>
      </div>
      {data.rows.length === 0 ? (
        <p className="text-sm text-slate-500">No ratings in this view.</p>
      ) : (
        <div className="space-y-1">
          {data.rows.map((r) => {
            const expanded = open === r.id;
            const pill = trustPill(r.trust_score);
            return (
              <div key={r.id} data-testid={`rating-${r.id}`} className="rounded-lg border border-slate-100">
                <button onClick={() => setOpen(expanded ? null : r.id)} className="flex w-full flex-wrap items-center justify-between gap-2 p-2 text-left hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <StarRow n={r.rating} />
                    <TonePill tone={statusTone[r.review_status] || "slate"}>{r.review_status || "APPROVED"}</TonePill>
                    <TonePill tone={pill.tone}>{pill.label}</TonePill>
                    {r.dispute && <TonePill tone={r.dispute.decision === "UPHELD" ? "emerald" : r.dispute.decision === "REJECTED" ? "rose" : "amber"}>Dispute: {r.dispute.status}</TonePill>}
                  </div>
                  <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()} {new Date(r.created_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</div>
                </button>
                {expanded && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-3 text-xs text-slate-700">
                    {r.comment && <div className="mb-2 italic text-slate-600">"{r.comment}"</div>}
                    {r.touchpoints && Object.keys(r.touchpoints).length > 0 && (
                      <div className="mb-2">
                        <div className="mb-1 font-semibold text-slate-900">Touchpoints</div>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.touchpoints).map(([k, v]) => (
                            <span key={k} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${v ? "bg-emerald-100 text-emerald-700" : v === false ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                              {k} {v === true ? "✓" : v === false ? "✗" : "—"}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.dispute?.decision_reason && <div className="rounded-lg bg-white p-2"><strong>Manager said:</strong> "{r.dispute.decision_reason}"</div>}
                    <div className="mt-2 text-[11px] text-slate-400">Rating ID: {r.id}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StarRow({ n }) {
  const v = Number(n) || 0;
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= v ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

// ========================= LEADERBOARD TAB =========================
function LeaderboardTab() {
  const [lb, setLb] = useState(null);
  useEffect(() => { api.get("/me/leaderboard?top=15").then((r) => setLb(r.data)).catch(() => {}); }, []);
  if (!lb) return <SkeletonBlock />;
  return (
    <>
      <div className="rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-indigo-50 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <Trophy className="h-6 w-6 text-amber-500" />
          <div>
            <h2 className="font-head text-lg font-bold text-slate-900">Leaderboard</h2>
            <p className="text-xs text-slate-500">
              Last 30 days · ranked by Service Index. Threshold-eligible people appear first; everyone else is listed below with the same ranking rules.
            </p>
          </div>
        </div>
      </div>

      <LeaderboardSection
        title={`Your location${lb.location?.name?.name ? ` — ${lb.location.name.name}` : ""}`}
        subtitle={lb.location.my_rank ? `You're ranked #${lb.location.my_rank} of ${lb.location.total_people}` : `${lb.location.total_people} teammates in your location`}
        entries={lb.location.entries}
        testid="leaderboard-location"
      />

      {lb.organization && (
        <LeaderboardSection
          title="Across the organisation"
          subtitle={lb.organization.my_location_rank ? `Your location ranks #${lb.organization.my_location_rank} of ${lb.organization.total_locations}` : `${lb.organization.total_locations} locations`}
          entries={lb.organization.entries}
          isLocation
          testid="leaderboard-org"
        />
      )}
    </>
  );
}

function LeaderboardSection({ title, subtitle, entries, isLocation, testid }) {
  const medal = ["#F59E0B", "#94A3B8", "#B45309"]; // gold / silver / bronze
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="mb-3">
        <div className="font-head text-sm font-bold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing to show yet.</p>
      ) : (
        <div className="space-y-1">
          {entries.map((e) => (
            <div key={e.id} data-testid={`lb-row-${e.rank}`}
              className={`flex items-center justify-between rounded-lg p-2 transition-colors ${
                (e.is_me || e.is_mine) ? "bg-indigo-50 ring-2 ring-indigo-200" : "hover:bg-slate-50"
              }`}>
              <div className="flex items-center gap-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-full font-head text-xs font-bold text-white"
                  style={{ background: e.rank <= 3 ? medal[e.rank - 1] : "#CBD5E1" }}>
                  {e.rank}
                </div>
                {!isLocation && (e.photo_url ? (
                  <img src={e.photo_url} alt={e.name} className="h-8 w-8 flex-none rounded-full object-cover" />
                ) : (
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                    {(e.name || "?").charAt(0)}
                  </div>
                ))}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {e.name}
                    {(e.is_me || e.is_mine) && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-indigo-500">You</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {isLocation ? (e.city || "") : (e.job_title || "")}
                    {e.sample > 0 && <span> · {e.sample} ratings</span>}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-head text-base font-bold text-slate-900">{e.ssi_percent}%</div>
                {!e.meets_threshold && <div className="text-[10px] font-semibold text-slate-400">below sample</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrendCard({ trend, summary }) {
  const icon = { up: TrendingUp, down: TrendingDown, flat: Minus }[summary.direction] || Minus;
  const Icon = icon;
  const color = { up: "text-emerald-600", down: "text-rose-600", flat: "text-slate-500" }[summary.direction];
  const bar = { up: "bg-emerald-500", down: "bg-rose-500", flat: "bg-slate-400" }[summary.direction];
  const max = Math.max(100, ...trend.map((p) => p.value || 0));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="trend-card">
      <div className="flex items-center justify-between">
        <div className="font-head text-sm font-bold text-slate-900">30-day trend</div>
        <div className={`flex items-center gap-1 text-xs font-semibold ${color}`}>
          <Icon className="h-3 w-3" /> {summary.direction === "flat" ? "Flat" : `${summary.delta_pts > 0 ? "+" : ""}${summary.delta_pts} pts`}
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500">{summary.copy}</p>
      {trend.length > 0 && (
        <div className="mt-3 flex h-24 items-end gap-1">
          {trend.map((p, i) => (
            <div key={i} className={`flex-1 rounded-t ${bar}`} style={{ height: `${Math.max(6, ((p.value || 0) / max) * 100)}%` }} title={`${p.date}: ${p.value}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function MonthlyProgressCard({ series }) {
  const max = Math.max(5, ...series.map((m) => m.avg || 0));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="progress-card">
      <div className="mb-2 flex items-center gap-2 font-head text-sm font-bold text-slate-900">
        <LineChart className="h-4 w-4" /> Monthly progress
      </div>
      <p className="mb-3 text-xs text-slate-500">Your average rating per month, last 12 months.</p>
      <div className="flex items-end gap-2" style={{ height: 120 }}>
        {series.map((m) => (
          <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
            <div className="w-full rounded-t bg-indigo-500" style={{ height: `${((m.avg || 0) / max) * 100}%`, minHeight: 6 }} title={`${m.month}: ${m.avg} (${m.sample} ratings)`} />
            <div className="text-[10px] font-semibold text-slate-500">{m.month.slice(5)}</div>
            <div className="text-[10px] font-bold text-slate-800">{m.avg}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========================= FAIR PLAY =========================
function FairPlayView({ f }) {
  const r = f.ratings;
  return (
    <>
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <Scale className="h-5 w-5 text-indigo-600" />
          <h2 className="font-head text-lg font-bold text-slate-900">Transparency & fair play</h2>
        </div>
        <p className="text-sm leading-relaxed text-slate-600">
          Everything about how your score is built is visible to you. No silent deletes.
          Every rating you've received, every dispute you've raised, and the exact math that produced your Service Index — all shown below.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total ratings" value={r.total_received} testid="fp-total" />
        <StatTile label="Verified" value={r.verified} testid="fp-verified" tone="emerald" />
        <StatTile label="Rejected" value={r.rejected} testid="fp-rejected" tone="rose" />
        <StatTile label="Disputed (open)" value={r.disputed_open} testid="fp-disputed-open" tone="amber" />
      </div>

      <ConfidenceCard band={f.confidence_band} score={f.score} />
      <ScoreExplainerCard steps={f.score_explainer} />
      <DisputesCard disputes={f.recent_disputes} ratings={r} />
    </>
  );
}

function ConfidenceCard({ band, score }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="confidence-card">
      <div className="mb-2 flex items-center gap-2">
        <Shield className="h-4 w-4 text-slate-500" />
        <div className="font-head text-sm font-bold text-slate-900">Confidence level: {band.label}</div>
      </div>
      <div className="h-3 w-full rounded-full bg-slate-100">
        <div className="h-3 rounded-full bg-indigo-500 transition-all" style={{ width: `${Math.min(100, Math.max(5, band.pct))}%` }} />
      </div>
      <p className="mt-2 text-xs text-slate-600">{band.copy}</p>
      <p className="mt-2 text-[11px] text-slate-400">
        {score.verified_interactions} verified · minimum sample {score.min_sample} · raw average {score.raw_average}
      </p>
    </div>
  );
}

function ScoreExplainerCard({ steps }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="explainer-card">
      <div className="mb-2 flex items-center gap-2">
        <Info className="h-4 w-4 text-slate-500" />
        <div className="font-head text-sm font-bold text-slate-900">How your score is calculated</div>
      </div>
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <div className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">{i + 1}</div>
            <div>
              <div className="text-sm font-semibold text-slate-900">{s.step}</div>
              <div className="text-xs text-slate-500">{s.detail}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DisputesCard({ disputes, ratings }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="disputes-card">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-slate-500" />
        <div className="font-head text-sm font-bold text-slate-900">Your disputes</div>
      </div>
      <div className="mb-3 flex gap-4 text-xs text-slate-500">
        <span><strong className="text-slate-800">{ratings.disputed_open}</strong> open</span>
        <span><strong className="text-emerald-700">{ratings.disputed_upheld}</strong> upheld in your favour</span>
        <span><strong className="text-rose-600">{ratings.disputed_rejected}</strong> rejected</span>
      </div>
      {disputes.length === 0 ? (
        <p className="text-sm text-slate-500">You haven't disputed any feedback. If a rating feels unfair, open the CV tab and tap "Dispute" on the feedback card.</p>
      ) : disputes.map((d) => (
        <div key={d.id} className="mb-2 rounded-lg bg-slate-50 p-3 text-sm" data-testid={`dispute-${d.id}`}>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900">{d.reason}</div>
            <StatusPill status={d.status} decision={d.decision} />
          </div>
          {d.decision_reason && <p className="mt-1 text-xs text-slate-600">Manager said: "{d.decision_reason}"</p>}
          <p className="mt-1 text-[11px] text-slate-400">Raised {new Date(d.created_at).toLocaleDateString()}</p>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status, decision }) {
  if (status === "RESOLVED") {
    const good = decision === "UPHELD";
    return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${good ? "bg-emerald-500" : "bg-rose-500"}`}>{good ? "Upheld ✓" : "Rejected"}</span>;
  }
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">Pending</span>;
}

// ========================= BADGES =========================
function BadgesGrid({ b }) {
  const earned = b.badges.filter((x) => x.earned).length;
  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-yellow-50 to-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <Trophy className="h-6 w-6 text-amber-500" />
          <div>
            <h2 className="font-head text-lg font-bold text-slate-900">Milestone badges</h2>
            <p className="text-xs text-slate-500">{earned} of {b.badges.length} earned · {b.stats.interactions} interactions · {b.stats.five_star} five-star moments · {b.stats.consistent_months} consistent months</p>
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {b.badges.map((bd) => {
          const Icon = BADGE_ICONS[bd.icon] || Trophy;
          return (
            <div key={bd.key} data-testid={`badge-${bd.key}`}
              className={`rounded-xl border p-4 text-center shadow-sm transition ${
                bd.earned ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white"
                          : "border-slate-200 bg-slate-50 opacity-60"
              }`}>
              <div className={`mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full ${bd.earned ? "bg-amber-100 text-amber-600" : "bg-slate-200 text-slate-400"}`}>
                {bd.earned ? <Icon className="h-7 w-7" /> : <Lock className="h-6 w-6" />}
              </div>
              <div className="font-head text-sm font-bold text-slate-900">{bd.title}</div>
              <div className="mt-1 text-xs text-slate-500">{bd.description}</div>
              {bd.earned && <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Earned</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ========================= COMMON =========================
function StatTile({ label, value, tone = "indigo", testid }) {
  const map = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-xl ${map[tone]} p-4 shadow-sm`} data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 font-head text-2xl font-bold">{value}</div>
    </div>
  );
}

function SkeletonBlock() {
  return <div className="h-40 animate-pulse rounded-xl bg-slate-100" />;
}

// ========================= REFER & EARN =========================
const REFERRAL_STATUS = {
  NEW:             { label: "Submitted",     tone: "slate",   copy: "We've got it — our team will reach out soon." },
  CONTACTED:       { label: "Contacted",     tone: "indigo",  copy: "Our team has reached out to your friend." },
  DEMO_SCHEDULED:  { label: "Demo scheduled", tone: "indigo", copy: "A demo is on the calendar." },
  DEMO_HELD:       { label: "Demo held ✓",   tone: "emerald", copy: "Meeting happened — reward accrued!" },
  SIGNED_UP:       { label: "Signed up ★",   tone: "amber",   copy: "They came on board — full reward unlocked!" },
  CLOSED_LOST:     { label: "Didn't convert", tone: "rose",   copy: "Didn't work out this time. Thanks for trying!" },
};

function ReferAndEarn({ r, onSubmitted }) {
  const s = r.settings;
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);
  const [shareForm, setShareForm] = useState({ referee_name: "", referee_business: "", referee_phone: "" });
  const [shareBusy, setShareBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    referee_name: "", referee_business: "", referee_phone: "", referee_email: "", referee_role: "", note: "",
  });

  const openShare = async () => {
    setShareOpen(true);
    setShareForm({ referee_name: "", referee_business: "", referee_phone: "" });
    setShareLinks(null);
    // Fetch base link immediately so the "Copy link" works even before a phone number
    setShareBusy(true);
    try {
      const { data } = await api.post("/me/referrals/share-link", {});
      setShareLinks(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setShareBusy(false); }
  };

  const regenerateShare = async (patch) => {
    const next = { ...shareForm, ...patch };
    setShareForm(next);
    setShareBusy(true);
    try {
      const { data } = await api.post("/me/referrals/share-link", {
        referee_name: next.referee_name.trim() || null,
        referee_business: next.referee_business.trim() || null,
        referee_phone: next.referee_phone.trim() || null,
      });
      setShareLinks(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setShareBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareLinks.landing_url); toast.success("Invite link copied!"); }
    catch { toast.error("Couldn't copy — tap the link to open it."); }
  };

  const submit = async () => {
    if (!form.referee_name.trim() || !form.referee_business.trim()) {
      toast.error("Please share your friend's name and their business.");
      return;
    }
    if (!form.referee_phone.trim() && !form.referee_email.trim()) {
      toast.error("Add at least a phone number or email so we can reach them.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/referrals", {
        referee_name: form.referee_name.trim(),
        referee_business: form.referee_business.trim(),
        referee_phone: form.referee_phone.trim() || null,
        referee_email: form.referee_email.trim() || null,
        referee_role: form.referee_role.trim() || null,
        note: form.note.trim() || null,
      });
      toast.success("Referral submitted — our team will take it from here.");
      setOpen(false);
      setForm({ referee_name: "", referee_business: "", referee_phone: "", referee_email: "", referee_role: "", note: "" });
      onSubmitted?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const money = (n) => `${s.currency || "R"} ${Number(n || 0).toLocaleString()}`;

  return (
    <div className="space-y-4" data-testid="refer-tab">
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <div className="mb-2 flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-500" />
              <h2 className="font-head text-lg font-bold text-slate-900">Refer a friend, earn cash</h2>
            </div>
            <p className="text-sm leading-relaxed text-slate-600">{s.programme_blurb}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-white p-3 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-wide text-indigo-500">If they take the demo</div>
                <div className="mt-1 font-head text-xl font-bold text-slate-900">{money(s.reward_meeting_amount)}</div>
                <div className="text-[11px] text-slate-500">paid to you</div>
              </div>
              <div className="rounded-lg bg-white p-3 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-wide text-amber-500">If they sign up</div>
                <div className="mt-1 font-head text-xl font-bold text-slate-900">{money(s.reward_signup_amount)}</div>
                <div className="text-[11px] text-slate-500">to you + the same to them</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button data-testid="share-link-btn" variant="outline" onClick={openShare}>
              <Share2 className="mr-1 h-4 w-4" />Share link
            </Button>
            <Button data-testid="new-referral-btn" onClick={() => setOpen(true)} className="bg-[#4338CA] hover:bg-[#3730A3]">
              <UserPlus className="mr-1 h-4 w-4" />Refer a friend
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Referrals sent" value={r.totals.count} testid="r-count" />
        <StatTile label="Accrued" value={money(r.totals.accrued)} testid="r-accrued" tone="indigo" />
        <StatTile label="Paid out" value={money(r.totals.paid)} testid="r-paid" tone="emerald" />
        <StatTile label="Outstanding" value={money(r.totals.outstanding)} testid="r-outstanding" tone="amber" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-500" />
          <div className="font-head text-sm font-bold text-slate-900">Your referrals</div>
        </div>
        {r.referrals.length === 0 ? (
          <p className="text-sm text-slate-500">You haven't referred anyone yet. Tap <strong>Refer a friend</strong> and share their details — we'll handle the rest.</p>
        ) : (
          <div className="space-y-2">
            {r.referrals.map((ref) => {
              const st = REFERRAL_STATUS[ref.status] || REFERRAL_STATUS.NEW;
              return (
                <div key={ref.id} data-testid={`referral-${ref.id}`} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">{ref.referee_name} <span className="text-xs font-normal text-slate-500">· {ref.referee_business}</span></div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {ref.referee_role && <span className="mr-2">{ref.referee_role}</span>}
                        {ref.referee_phone && <span className="mr-2">{ref.referee_phone}</span>}
                        {ref.referee_email && <span>{ref.referee_email}</span>}
                      </div>
                      {ref.note && <p className="mt-1 text-xs italic text-slate-500">"{ref.note}"</p>}
                    </div>
                    <div className="text-right">
                      <TonePill tone={st.tone}>{st.label}</TonePill>
                      {(ref.payout_total || 0) > 0 && (
                        <div className="mt-1 text-xs font-bold text-slate-700">
                          {money(ref.payout_total)}{" "}
                          <span className={ref.payout_status === "PAID" ? "text-emerald-600" : "text-amber-600"}>
                            {ref.payout_status === "PAID" ? "paid" : "pending"}
                          </span>
                        </div>
                      )}
                      <div className="mt-1 text-[10px] text-slate-400">{new Date(ref.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">{st.copy}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>Refer a friend</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Our team will contact them to set up a short, no-pressure demo. You and your friend both earn when they come on board.</p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Their name <span className="text-rose-500">*</span></label>
              <Input value={form.referee_name} onChange={(e) => setForm({ ...form, referee_name: e.target.value })}
                placeholder="e.g. Lebo Mahlangu" data-testid="refer-name" required />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Their business <span className="text-rose-500">*</span></label>
              <Input value={form.referee_business} onChange={(e) => setForm({ ...form, referee_business: e.target.value })}
                placeholder="e.g. The Local Kitchen" data-testid="refer-business" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Their role</label>
                <Input value={form.referee_role} onChange={(e) => setForm({ ...form, referee_role: e.target.value })}
                  placeholder="Waiter, Manager, Owner…" data-testid="refer-role" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Phone</label>
                <Input type="tel" inputMode="tel" value={form.referee_phone}
                  onChange={(e) => setForm({ ...form, referee_phone: e.target.value })}
                  placeholder="+27…" data-testid="refer-phone" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Email</label>
              <Input type="email" inputMode="email" value={form.referee_email}
                onChange={(e) => setForm({ ...form, referee_email: e.target.value })}
                placeholder="their@email.com" data-testid="refer-email" />
              <p className="mt-1 text-[11px] text-slate-400">Add at least a phone number or an email.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">A quick note (optional)</label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="What makes them great? Any best time to call?" className="min-h-[70px]" data-testid="refer-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="submit-referral" className="bg-[#4338CA] hover:bg-[#3730A3]">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              {busy ? "Submitting…" : "Submit referral"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog" className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>Share your invite link</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Pre-fill your friend's details so they land on a form that already knows who sent them.</p>
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Their name <span className="text-xs font-normal text-slate-400">(optional)</span></label>
                <Input value={shareForm.referee_name} onChange={(e) => regenerateShare({ referee_name: e.target.value })} placeholder="Lebo" data-testid="share-name" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Business</label>
                <Input value={shareForm.referee_business} onChange={(e) => regenerateShare({ referee_business: e.target.value })} placeholder="The Local Kitchen" data-testid="share-business" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Their mobile <span className="text-xs font-normal text-slate-400">(we'll address WhatsApp/SMS to them)</span></label>
              <Input type="tel" inputMode="tel" value={shareForm.referee_phone} onChange={(e) => regenerateShare({ referee_phone: e.target.value })} placeholder="+27 82 555 1234" data-testid="share-phone" />
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="share-preview">
            {shareBusy ? <span className="inline-flex items-center gap-1 text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Preparing your link…</span> : (shareLinks?.message || "")}
          </div>

          {shareLinks && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <a href={shareLinks.whatsapp_url} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
                className="flex flex-col items-center gap-1 rounded-xl bg-emerald-500 p-3 text-white hover:bg-emerald-600">
                <MessageCircle className="h-5 w-5" />
                <span className="text-xs font-bold">WhatsApp</span>
              </a>
              <a href={shareLinks.sms_url} data-testid="share-sms"
                className="flex flex-col items-center gap-1 rounded-xl bg-indigo-600 p-3 text-white hover:bg-indigo-700">
                <MessageSquare className="h-5 w-5" />
                <span className="text-xs font-bold">SMS</span>
              </a>
              <a href={shareLinks.mailto_url} data-testid="share-email"
                className="flex flex-col items-center gap-1 rounded-xl bg-slate-700 p-3 text-white hover:bg-slate-800">
                <Mail className="h-5 w-5" />
                <span className="text-xs font-bold">Email</span>
              </a>
              <button onClick={copyLink} type="button" data-testid="share-copy"
                className="flex flex-col items-center gap-1 rounded-xl bg-amber-500 p-3 text-white hover:bg-amber-600">
                <Copy className="h-5 w-5" />
                <span className="text-xs font-bold">Copy link</span>
              </button>
            </div>
          )}

          {shareLinks && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 text-xs">
              <LinkIcon className="h-3 w-3 flex-none text-slate-400" />
              <code className="flex-1 truncate text-slate-600" data-testid="share-url">{shareLinks.landing_url}</code>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShareOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TonePill({ tone, children }) {
  const map = {
    slate: "bg-slate-200 text-slate-700",
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${map[tone] || map.slate}`}>{children}</span>;
}
